type UnknownRecord = Record<string, unknown>;

type CourseContext = {
  id: string;
  title: string;
  url: string;
  source: string;
  capturedAt: string;
};

type LeadInterestDetailsProps = {
  metadata?: UnknownRecord | null;
};

function asRecord(value: unknown): UnknownRecord | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as UnknownRecord;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function formatLabel(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function toWebsiteUrl(value: string): string {
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }

  return `https://cosmetic.college${value.startsWith('/') ? value : `/${value}`}`;
}

function parseCourse(value: unknown): CourseContext | null {
  if (typeof value === 'string') {
    const title = value.trim();
    return title
      ? { id: '', title, url: '', source: '', capturedAt: '' }
      : null;
  }

  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const title =
    asString(record.title) ||
    asString(record.name) ||
    asString(record.course_title);
  const id =
    asString(record.event_detail_id) ||
    asString(record.id) ||
    asString(record.code);

  if (!title && !id) {
    return null;
  }

  return {
    id,
    title: title || id,
    url: asString(record.url) || asString(record.path),
    source: asString(record.source),
    capturedAt: asString(record.captured_at) || asString(record.capturedAt),
  };
}

function parseCourses(value: unknown): CourseContext[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(parseCourse).filter(Boolean) as CourseContext[];
}

function dedupeCourses(courses: CourseContext[]): CourseContext[] {
  const seen = new Set<string>();

  return courses.filter((course) => {
    const key = (course.id || course.title).toLowerCase();
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function getInterestData(metadata?: UnknownRecord | null) {
  const record = asRecord(metadata) || {};
  const preferences = asRecord(record.preferences) || {};
  const courses = dedupeCourses(parseCourses(record.courses));
  const recentCourses = dedupeCourses(parseCourses(preferences.recent_courses));
  const listingCategory = asString(preferences.listing_category);
  const listingSourcePage =
    asString(preferences.listing_source_page) ||
    (listingCategory ? asString(preferences.source_page) : '');
  const callbackHelpWith =
    asString(preferences.callback_help_with) ||
    asString(record.callback_help_with);
  const lastCourseTitle =
    asString(preferences.last_course_title) ||
    asString(record.last_course_title);
  const lastEventDetailId =
    asString(preferences.last_event_detail_id) ||
    asString(record.last_event_detail_id);
  const lastCoursePage =
    asString(preferences.last_course_page) || asString(record.last_course_page);

  const hasLastCourse =
    Boolean(lastCourseTitle || lastEventDetailId) &&
    !courses.some(
      (course) =>
        (lastEventDetailId && course.id === lastEventDetailId) ||
        (lastCourseTitle && course.title === lastCourseTitle),
    );

  const primaryCourses = hasLastCourse
    ? [
        ...courses,
        {
          id: lastEventDetailId,
          title: lastCourseTitle || lastEventDetailId,
          url: lastCoursePage,
          source: '',
          capturedAt: '',
        },
      ]
    : courses;

  const primaryKeys = new Set(
    primaryCourses.map((course) => (course.id || course.title).toLowerCase()),
  );
  const browsingHistory = recentCourses.filter(
    (course) => !primaryKeys.has((course.id || course.title).toLowerCase()),
  );

  return {
    primaryCourses,
    browsingHistory,
    listingCategory,
    listingSourcePage,
    callbackHelpWith,
  };
}

export function getLeadInterestLabel(metadata?: UnknownRecord | null): string {
  const { primaryCourses, listingCategory } = getInterestData(metadata);

  if (primaryCourses.length === 1) {
    return primaryCourses[0].title;
  }

  if (primaryCourses.length > 1) {
    return `${primaryCourses.length} courses`;
  }

  return listingCategory ? `${formatLabel(listingCategory)} listing` : '';
}

function CourseItem({
  course,
  compact = false,
}: {
  course: CourseContext;
  compact?: boolean;
}) {
  const content = (
    <>
      <span className="block font-medium text-gray-900">{course.title}</span>
      {!compact && course.id ? (
        <span className="mt-0.5 block break-all font-mono text-[11px] text-gray-500">
          {course.id}
        </span>
      ) : null}
    </>
  );

  return (
    <li className="min-w-0">
      {course.url ? (
        <a
          href={toWebsiteUrl(course.url)}
          target="_blank"
          rel="noreferrer"
          className="group block rounded-md outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
        >
          {content}
          <span className="mt-1 inline-block text-xs font-medium text-teal-700 group-hover:underline">
            Open course page
          </span>
        </a>
      ) : (
        content
      )}
    </li>
  );
}

export default function LeadInterestDetails({
  metadata,
}: LeadInterestDetailsProps) {
  const {
    primaryCourses,
    browsingHistory,
    listingCategory,
    listingSourcePage,
    callbackHelpWith,
  } = getInterestData(metadata);

  if (
    primaryCourses.length === 0 &&
    browsingHistory.length === 0 &&
    !listingCategory &&
    !callbackHelpWith
  ) {
    return null;
  }

  const contextLabel =
    primaryCourses.length > 0 ? 'Specific course interest' : 'Course listing';

  return (
    <div className="sm:col-span-2">
      <dt className="mb-4 text-sm font-medium text-gray-500">
        Enquiry context
      </dt>
      <dd className="overflow-hidden rounded-lg border border-teal-200 bg-teal-50/60">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-teal-200 px-4 py-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-teal-800">
              What prompted this enquiry
            </p>
            <p className="mt-0.5 text-xs text-gray-600">
              Captured from the website before the lead was submitted.
            </p>
          </div>
          <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-teal-800 ring-1 ring-teal-200">
            {contextLabel}
          </span>
        </div>

        <div className="grid gap-5 p-4 sm:grid-cols-2">
          {primaryCourses.length > 0 ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-teal-800">
                {primaryCourses.length === 1 ? 'Course' : 'Courses'}
              </p>
              <ul className="space-y-3 text-sm">
                {primaryCourses.map((course) => (
                  <CourseItem
                    key={`${course.id}-${course.title}`}
                    course={course}
                  />
                ))}
              </ul>
            </div>
          ) : null}

          {listingCategory ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-teal-800">
                Listing interest
              </p>
              <p className="text-sm font-medium text-gray-900">
                {formatLabel(listingCategory)}
              </p>
              {listingSourcePage ? (
                <a
                  href={toWebsiteUrl(listingSourcePage)}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-block text-xs font-medium text-teal-700 hover:underline"
                >
                  Open source page
                </a>
              ) : null}
            </div>
          ) : null}

          {callbackHelpWith ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-teal-800">
                Asked for help with
              </p>
              <p className="text-sm font-medium text-gray-900">
                {formatLabel(callbackHelpWith)}
              </p>
            </div>
          ) : null}

          {browsingHistory.length > 0 ? (
            <div className="sm:col-span-2">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-teal-800">
                Other recently viewed courses
              </p>
              <ul className="grid gap-2 text-sm sm:grid-cols-2">
                {browsingHistory.map((course) => (
                  <CourseItem
                    key={`${course.id}-${course.title}`}
                    course={course}
                    compact
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </dd>
    </div>
  );
}
